import AxiosInstance from "@/api/axios";
import { IRegion } from "@/pages/Settings/RegionSettingsForm";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

let abortController: AbortController | null = null;

export const fetchRegions = createAsyncThunk(
  "regions/fetchData",
  async ({ client }: { client: string }, { rejectWithValue }) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    try {
      const response = await AxiosInstance.get(`/region/${client}`, {
        signal: abortController.signal,
      });

      if (response.data.success) {
        return { region: response.data.data };
      } else {
        return rejectWithValue("Failed to fetch regions");
      }
    } catch (error: any) {
      // console.log(error);
      if (abortController.signal.aborted || error.code === "ERR_CANCELED") {
        return null;
      }
      return rejectWithValue(error.message || "Failed to fetch regions");
    } finally {
      abortController = null;
    }
  },
);

export const updateRegion = createAsyncThunk(
  "regions/updateRegion",
  async ({ regionData }: { regionData: IRegion }, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.patch(
        `/region/${regionData.client}`,
        regionData,
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        return rejectWithValue("Failed to update region");
      }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update region",
      );
    }
  },
);

interface RegionState {
  region: IRegion[] | null;
  loading: boolean;
  error: string | null;
}

const initialState: RegionState = {
  region: null,
  loading: false,
  error: null,
};

export const addRegion = createAsyncThunk(
  "regions/addRegion",
  async (regionData: IRegion, { rejectWithValue }) => {
    try {
      return regionData;
    } catch (error: any) {
      return rejectWithValue("Failed to add region");
    }
  },
);
const regionsReducer = createSlice({
  name: "regions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRegions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRegions.fulfilled, (state, action) => {
        state.loading = false;
        state.region = action.payload ? action.payload.region : null;
      })
      .addCase(fetchRegions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload
          ? (action.payload as string)
          : "Unknown error";
      })
      .addCase(addRegion.fulfilled, (state, action) => {
        state.region = state.region
          ? [...state.region, action.payload]
          : [action.payload];
      })
      .addCase(updateRegion.fulfilled, (state, action) => {
        if (state.region) {
          state.region = state.region.map((r) =>
            r._id === action.payload._id ? action.payload : r,
          );
        }
      })
      .addCase(updateRegion.rejected, (state, action) => {
        state.error = action.payload
          ? (action.payload as string)
          : "Failed to update region";
      });
  },
});

export default regionsReducer.reducer;
